import type { MatchState, PlayerId } from '@signalclash/shared';
import { isOperational } from '@signalclash/shared';

import type { Selection, SelectionRef } from './Selection.js';

// V1.8 UI redesign helper extracted from InputRouter so the router stays
// under the max-lines=600 cap. The function encodes a single rule: when
// the player presses B and nothing relevant is selected, auto-select
// their own operational Hub (if any) or their Core so the now-contextual
// BuildPanel reveals itself. Pure read of MatchState + Selection; no
// command dispatch — selection is a presentation concern (see
// Selection.ts header for the rule).
export function ensureBuildContextSelected(
  state: MatchState | null,
  selection: Selection,
  ownerId: PlayerId,
): void {
  if (state === null) return;
  const current: SelectionRef | null = selection.getCurrent();
  if (current !== null) {
    if (current.kind === 'core') {
      const c = state.cores[current.id];
      if (c !== undefined && c.ownerId === ownerId) return;
    } else if (current.kind === 'building') {
      const b = state.buildings[current.id];
      if (
        b !== undefined &&
        b.ownerId === ownerId &&
        b.kind === 'production_hub' &&
        isOperational(b)
      ) {
        return;
      }
    }
  }
  // Prefer an operational Hub (typically placed forward, so building from
  // it puts new defenses near the front line); fall back to the Core when
  // no Hub exists. Both produce — the choice here is which build-context
  // panel pops up, not which of the two queues is "active".
  for (const b of Object.values(state.buildings)) {
    if (
      b.ownerId === ownerId &&
      b.kind === 'production_hub' &&
      isOperational(b)
    ) {
      selection.selectBuilding(b.id);
      return;
    }
  }
  for (const c of Object.values(state.cores)) {
    if (c.ownerId === ownerId) {
      selection.selectCore(c.id);
      return;
    }
  }
}
